<div #dropdownRef class="relative inline-block w-full" [class]="className" [style.width]="width">
  <!-- Title -->
  @if (title) {
  <label [class]="applyThemeClasses(titleClass + ' block mb-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 transition-colors')">
    {{ title }}
    @if (required) {
    <span class="text-red-500 ml-0.5">*</span>
    }
  </label>
  }

  <!-- Custom Trigger -->
  @if (hasCustomTrigger()) {
  <div
    class="inline-block"
    role="button"
    tabindex="0"
    [attr.aria-expanded]="isOpen()"
    [attr.aria-haspopup]="true"
    [attr.aria-disabled]="disabled"
    (click)="onCustomTriggerClick($event)"
    (keydown)="onCustomTriggerKeydown($event)"
    (blur)="onBlur()"
  >
    <ng-content select="[appDropdownTrigger]"></ng-content>
  </div>
  } @else {
  <!-- Default Trigger -->
  <button type="button" [class]="getDropdownTriggerClasses()" [disabled]="disabled" [attr.aria-expanded]="isOpen()"
    [attr.aria-haspopup]="true" (click)="toggle()" (blur)="onBlur()"
    [style.height]="cleanCustomHeight"
    [style.minHeight]="cleanCustomHeight"
    [style.width]="cleanCustomWidth">
    <!-- Selected Content -->
    <div class="flex-1 flex min-w-0 items-center justify-between overflow-hidden">
      <!-- Selected Items Display -->
      <div class="flex-1 min-w-0 overflow-hidden">
        @if (selectedOptions().length > 0 || (type === 'datepicker' &&
        (selectedYear() || selectedMonth()))) { @if (selectionMode === 'single'
        || type === 'datepicker') { @let firstOption = selectedOptions()[0];
        <div class="flex items-center gap-2">
          @if (firstOption && firstOption.image) {
          <img [src]="firstOption.image" class="w-4 h-4 rounded-sm object-cover" alt="icon" />
          } @else if (firstOption && firstOption.icon) {
            @if (useImageForIcon) {
              <img [src]="firstOption.icon" class="w-6 h-6 rounded-sm object-cover" alt="icon" />
            } @else {
              <app-icon-v2 [name]="firstOption.icon" [size]="triggerIconSize" [class]="applyThemeClasses(iconClass || 'text-gray-500 dark:text-gray-400')" />
            }
          }
          <app-tooltip [content]="firstOption?.tooltip || firstOption?.label || displayText()" position="top" 
            [useFixedStrategy]="tooltipFixedStrategy" [maxWidth]="tooltipMaxWidth" [className]="'whitespace-nowrap !text-xs'"
            [isMarkdown]="tooltipIsMarkdown" [disabled]="!showTooltip" [flexCenter]="true">
            <span class="flex min-w-0 max-w-full gap-2 overflow-hidden whitespace-nowrap" [class]="applyThemeClasses(textClass || 'text-gray-900 dark:text-white')">
            @if(iconName){
              @if (useImageForIcon) {
                <img [src]="iconName" class="w-4 h-4 rounded-sm object-cover" alt="icon" />
              } @else {
                <app-icon-v2 [name]="iconName" [size]="triggerIconSize" [class]="applyThemeClasses(iconClass || 'text-gray-500 dark:text-white')" />
              }
            } <span class="min-w-0 truncate">{{ firstOption?.shortLabel || displayText() }}</span></span>
          </app-tooltip>
          <div class="flex gap-1">
            @if (firstOption && firstOption.badge) {
            <span [class]="getBadgeClasses(firstOption.badge.variant)">
              {{ firstOption.badge.text }}
            </span>
            } @if (firstOption && firstOption.badges) { @for (badge
            of firstOption.badges; track badge) {
            <span [class]="getBadgeClasses(badge.variant)">
              {{ badge.text }}
            </span>
            } }
          </div>
        </div>
        }
        <!-- <span class="text-gray-900 dark:text-white">{{ displayText() }}</span>
          <div class="flex gap-1">
            @if (selectedOptions()[0]?.badge) {
            <span [class]="getBadgeClasses(selectedOptions()[0]?.badge?.variant)">
              {{ selectedOptions()[0].badge?.text }}
            </span>
            }
            @for (badge of selectedOptions()[0]?.badges; track badge) {
            <span [class]="getBadgeClasses(badge.variant)">
              {{ badge.text }}
            </span>
            }
          </div>
        </div> -->
        @if (selectionMode === 'multiple') {
        <div class="dropdown-selected-multiple">
          <span class="dropdown-selected-text">{{ displayText() }}</span>
        </div>
        } }

        <!-- @if (selectionMode === 'multiple') {
        <div class="dropdown-selected-multiple">
          <span class="dropdown-selected-text">{{ displayText() }}</span>
        </div>
        } -->
        @else {
        <span [ngClass]="placeholderColor" class="flex gap-2 dropdown-placeholder"
          [class]="applyThemeClasses(textClass || (theme?.textColor || 'text-gray-500 dark:text-gray-400'))">
          @if(iconName){
          <app-icon-v2 [name]="iconName" [size]="triggerIconSize"
            [class]="applyThemeClasses(iconClass || (theme?.textColor || 'text-gray-500 dark:text-white'))" />
          } {{ placeholder }}
        </span>
        }
      </div>

      <!-- Actions -->
      <div class="dropdown-actions flex items-center gap-1">
        <!-- Clear Button -->
        @if (clearable && selectedValues().length > 0 && !disabled) {
        <button type="button" class="dropdown-clear" (click)="clearSelection(); $event.stopPropagation()"
          [attr.aria-label]="'Clear selection'">
          <app-icon-v2 [name]="clearIcon" size="16" />
        </button>
        }

        <!-- Loading Spinner -->
        @if (loading) {
        <div class="dropdown-spinner" aria-hidden="true">
          <div class="spinner"></div>
        </div>
        }

        <!-- Chevron Icon -->
        @if (!loading) {
        <app-icon-v2 [name]="chevronIcon" [size]="16" class="dropdown-chevron" [class]="iconClass || ''"
          [class.dropdown-chevron--rotated]="isOpen()" />
        }
      </div>
    </div>
  </button>
  }

  <!-- Dropdown Menu -->
  @if (isOpen()) {
  <div #menuRef [class]="getDropdownMenuClasses()" [style.maxHeight]="maxHeight"
    [style.top]="menuFixed && fixedMenuCoords() ? fixedMenuCoords()!.top + 'px' : null"
    [style.left]="menuFixed && fixedMenuCoords() ? fixedMenuCoords()!.left + 'px' : null"
    [style.width]="menuFixed && fixedMenuCoords()?.width ? fixedMenuCoords()!.width + 'px' : null"
    [style.maxWidth]="menuFixed && fixedMenuCoords()?.width ? fixedMenuCoords()!.width + 'px' : null"
    [style.minWidth]="menuFixed && fixedMenuCoords()?.width ? fixedMenuCoords()!.width + 'px' : null"
    [@dropdownAnimation]="isOpen() ? 'open' : 'closed'" role="listbox" (scroll)="onScroll($event)"
    [attr.aria-multiselectable]="selectionMode === 'multiple'">
    
    <!-- Menu Title -->
    @if (menuTitle) {
      <div [class]="applyThemeClasses(menuTitleClass || 'px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 border-b border-gray-100 dark:border-gray-700/50 mb-1')">
        {{ menuTitle }}
      </div>
    }

    <!-- Search Input -->
    <!-- @if (searchable) {
    <div class="dropdown-search">
      <div class="dropdown-search-wrapper">
        <app-icon-v2 [name]="searchIcon" size="16" class="dropdown-search-icon" />
        <input type="text" class="dropdown-search-input" [placeholder]="searchPlaceholder" [value]="searchQuery()"
          (input)="onSearchInput($event)" autocomplete="off" />
      </div>
    </div>
    } -->

    <!-- DatePicker Content -->
    @if (type === 'datepicker') {
    <div class="flex" role="none">
      <!-- Year Selection (Left Side) -->
      <div [class]="applyThemeClasses('flex-1 border-r border-slate-200 dark:border-slate-700')">
        <div [class]="applyThemeClasses('px-3 py-2 bg-slate-50 dark:bg-slate-700 border-b border-slate-200 dark:border-slate-600')">
          <h4 [class]="applyThemeClasses('text-sm font-medium text-slate-700 dark:text-slate-300')">
            Year
          </h4>
        </div>
        <div class="max-h-48 overflow-y-auto">
          @for (year of years(); track year) {
          <button type="button" (click)="selectYear(year)"
            class="w-full px-3 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors duration-150"
            [class]="
              selectedYear() === year
                ? 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                : 'text-slate-700 dark:text-slate-300'
            ">
            {{ year }}
            @if (selectedYear() === year) {
            <app-icon-v2 name="check" size="14" [class]="applyThemeClasses('float-right mt-1 text-purple-600 dark:text-purple-400')">
            </app-icon-v2>
            }
          </button>
          }
        </div>
      </div>

      <!-- Month Selection (Right Side) -->
      <div class="flex-1">
        <div [class]="applyThemeClasses('px-3 py-2 bg-slate-50 dark:bg-slate-700 border-b border-slate-200 dark:border-slate-600')">
          <h4 [class]="applyThemeClasses('text-sm font-medium text-slate-700 dark:text-slate-300')">
            Month
          </h4>
        </div>
        <div class="max-h-48 overflow-y-auto">
          @for (month of months(); track month.value) {
          <button type="button" (click)="selectMonth(month.value)"
            class="w-full px-3 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors duration-150"
            [class]="
              selectedMonth() === month.value
                ? 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                : 'text-slate-700 dark:text-slate-300'
            ">
            {{ month.label }}
            @if (selectedMonth() === month.value) {
            <app-icon-v2 name="check" size="14" [class]="applyThemeClasses('float-right mt-1 text-purple-600 dark:text-purple-400')">
            </app-icon-v2>
            }
          </button>
          }
        </div>
      </div>
    </div>
    }

    <!-- DatePicker Close Button -->
    <div *ngIf="type === 'datepicker'"
      [class]="applyThemeClasses('px-3 py-2 gap-2 bg-slate-50 dark:bg-slate-700 border-t border-slate-200 dark:border-slate-600 flex justify-end')">
      @if(isApplyButtonShown){
      <button [disabled]="!selectedYear()" type="button" (click)="applyYearMonthChanges()"
        class="cursor-pointer px-3 bg-[#AD46FF] text-white hover:bg-[#AD46FF]/80 py-1 text-xs hover:text-slate-800 dark:hover:text-slate-200 transition-colors rounded">
        Apply
      </button>
      }
      <button type="button" (click)="close()"
        class="cursor-pointer bg-[#AD46FF] text-white hover:bg-[#AD46FF]/80 rounded px-3 py-1 text-xs hover:text-slate-800 dark:hover:text-slate-200 transition-colors">
        {{ closeButtonText }}
      </button>
    </div>

    <!-- Search Input -->
    @if (searchable) {
    <div [class]="getSearchContainerClasses()">
      <div class="relative">
        <app-icon-v2 name="search" size="16" class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input type="text"
          [class]="getSearchInputClasses()"
          [placeholder]="searchPlaceholder" [value]="searchQuery()" (input)="onSearchInput($event)" autocomplete="off" />
      </div>
    </div>
    }

    <!-- Selection Options List -->
    @if (type === 'selection') {
    <div role="none">
      <!-- Loading State (Initial or Search) -->
      @if (loading && filteredOptions().length === 0) {
      <div class="flex flex-col items-center justify-center py-8 text-gray-500 dark:text-gray-400">
        <div class="spinner mb-2"></div>
        <span class="text-sm">{{ loadingMessage }}</span>
      </div>
      }

      <!-- Empty State -->
      @if (!loading && selectableOptions().length === 0 && actionOptions().length === 0) {
      <div class="flex items-center justify-center py-8 text-gray-500 dark:text-gray-400">
        <span class="text-sm">{{ emptyMessage }}</span>
      </div>
      }

      <!-- Options -->
      @if (selectableOptions().length > 0) { @for (option of
      selectableOptions(); track option.value || option.label || i; let i = $index) {
      <button type="button"
        class="w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150"
        [class]="getOptionClasses(option, i) + ' ' + getDragClasses(i)" [class.cursor-move]="
          draggable && !option.disabled && option.draggable !== false
        " [class.cursor-pointer]="
          !draggable || option.disabled || option.draggable === false
        " [disabled]="option.disabled" [attr.data-option-index]="i" [attr.aria-selected]="isSelected(option)"
        [attr.draggable]="
          draggable && !option.disabled && option.draggable !== false
        " role="option" (click)="selectOption(option)" (dragstart)="onDragStart($event, i, option)"
        (dragover)="onDragOver($event, i, option)" (dragenter)="onDragEnter($event, i, option)"
        (dragleave)="onDragLeave($event)" (drop)="onDrop($event, i, option)" (dragend)="onDragEnd($event)" [@fadeInOut]>
        <!-- Drag Handle (Optional) -->
        @if (draggable && showDragHandle && !option.disabled && option.draggable
        !== false) {
        <div
          [class]="applyThemeClasses('flex-shrink-0 cursor-move text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300')">
          <app-icon-v2 [name]="dragHandleIcon" size="16" />
        </div>
        }

        <!-- Selection Indicator -->
        @if (selectionStyle !== 'none') {
        <div class="flex-shrink-0 w-4 flex justify-center">
          @if (isSelected(option)) {
          <app-icon-v2 [name]="getSelectionIcon()" [size]="16" [class]="applyThemeClasses('text-purple-600 dark:text-purple-400')" />
          } @if (!isSelected(option) && (selectionStyle === 'radio' ||
          selectionStyle === 'checkbox')) {
          <div [class]="applyThemeClasses('w-4 h-4 border border-gray-300 dark:border-gray-600 rounded')"
            [class.rounded-full]="selectionStyle === 'radio'"></div>
          }
        </div>
        }

        <!-- Option Content -->
        <div class="flex-1 flex items-center gap-2">
          <!-- Option Icon/Image -->
          @if (option.image) {
          <img [src]="option.image" class="w-5 h-5 rounded-sm object-cover" alt="flag" />
          } @else if (option.icon) {
            @if (useImageForIcon) {
              <img [src]="option.icon" class="w-5 h-5 rounded-sm object-cover" alt="icon" />
            } @else {
              <app-icon-v2 [name]="option.icon" [size]="16" [class]="applyThemeClasses(option.iconClass || 'text-gray-500 dark:text-gray-400')" />
            }
          }

          <!-- Option Text -->
          <div class="dropdown-option-text flex flex-col w-full min-w-0">
            <app-tooltip [content]="option.tooltip || option.label" position="top" 
              [useFixedStrategy]="tooltipFixedStrategy" [maxWidth]="tooltipMaxWidth" [className]="'whitespace-nowrap !text-xs'"
              [isMarkdown]="tooltipIsMarkdown" [disabled]="!showTooltip">
              <div class="dropdown-option-label font-medium" [class.truncate]="!wrapLabels"
              [class.whitespace-normal]="wrapLabels" [class.break-words]="wrapLabels"
              [class]="applyThemeClasses(theme?.textColor || 'text-gray-900 dark:text-white')">{{ option.label }}</div>
            </app-tooltip>
            @if (option.description) {
            <app-tooltip [content]="option.tooltip || option.description" position="top" 
              [useFixedStrategy]="true" [maxWidth]="'480px'" [className]="'!text-xs'"
              [isMarkdown]="tooltipIsMarkdown" [disabled]="!showTooltip">
              <div [class]="applyThemeClasses('dropdown-option-description text-xs text-purple-600 dark:text-purple-400')"
              [class.truncate]="!wrapLabels" [class.whitespace-normal]="wrapLabels" [class.break-words]="wrapLabels">
              {{ option.description }}
            </div>
            </app-tooltip>
            } @if(isDelete){
            <app-icon-v2 [name]="clearIcon" (click)="deleteItem(option)" size="16"
              [class]="applyThemeClasses('text-purple-600 dark:text-purple-400')" />
            }
          </div>

          <!-- Option Badge(s) -->
          <div class="flex gap-1">
            @if (option.badge) {
            <span [class]="getBadgeClasses(option.badge.variant)">
              {{ option.badge.text }}
            </span>
            } @for (badge of option.badges; track badge) {
            <span [class]="getBadgeClasses(badge.variant)">
              {{ badge.text }}
            </span>
            }
          </div>
        </div>
      </button>
      }

      <!-- Action Footer -->
      @if (actionOptions().length > 0) {
      <div
        [class]="applyThemeClasses('sticky bottom-0 border-t border-[#EAEAEA] dark:border-line bg-white dark:bg-surface-1 p-2')"
        role="none"
      >
        @for (option of actionOptions(); track option.value) {
        <button
          type="button"
          class="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-150 bg-transparent text-[#AD46FF] border border-[#AD46FF] hover:bg-[#AD46FF]/5 dark:hover:bg-white/5"
          [disabled]="option.disabled"
          role="option"
          (click)="selectOption(option)"
        >
          @if (option.icon) {
          <app-icon-v2 [name]="option.icon" [size]="16" />
          }
          <span>{{ option.label }}</span>
        </button>
        }
      </div>
      }

      <!-- Appended Loading State -->
      @if (loading) {
      <div [class]="applyThemeClasses('flex items-center justify-center py-3 border-t border-gray-100 dark:border-gray-700')">
        <div class="spinner scale-75"></div>
        <span [class]="applyThemeClasses('ml-2 text-xs text-gray-500 dark:text-gray-400')">{{ loadingMessage }}</span>
      </div>
      }
      }
    </div>
    <!-- Apply Button Section -->
    @if (showApplyButton && type === 'selection') {
    <div
      [class]="applyThemeClasses('px-3 py-2 bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 flex items-center justify-end')">
      <!-- Apply Button -->
      <button type="button" (click)="applyChanges()" [disabled]="!hasChanges()"
        class="px-4 py-1.5 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 disabled:cursor-not-allowed rounded-md transition-colors duration-150 flex items-center justify-center gap-1.5 cursor-pointer">
        <app-icon-v2 name="check" size="14" />
        <span>{{ applyButtonText }}</span>
      </button>
    </div>
    } }

    <!-- Empty State -->
    <!-- @if (!loading && filteredOptions().length === 0) {
        <div class="flex items-center justify-center py-8 text-gray-500 dark:text-gray-400">
          <span class="text-sm">{{ emptyMessage }}</span>
        </div>
        } -->

    <!-- Options -->
    <!-- @if (!loading && filteredOptions().length > 0) {
        @for (option of filteredOptions(); track option; let i = $index) {
        <button type="button"
          class="w-full flex items-center gap-3 px-3 py-2.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-150"
          [class]="getOptionClasses(option, i)" [disabled]="option.disabled" [attr.data-option-index]="i"
          [attr.aria-selected]="isSelected(option)" role="option" (click)="selectOption(option)" [@fadeInOut]> -->
    <!-- Selection Indicator -->
    <!-- <div class="flex-shrink-0 w-4 flex justify-center">
            @if (isSelected(option)) {
            <app-icon-v2 [name]="getSelectionIcon()" size="16" class="text-purple-600 dark:text-purple-400" />
            }
            @if (!isSelected(option) && (selectionStyle === 'radio' || selectionStyle === 'checkbox')) {
            <div class="w-4 h-4 border border-gray-300 dark:border-gray-600 rounded"
              [class.rounded-full]="selectionStyle === 'radio'"></div>
            }
          </div> -->

    <!-- Option Content -->
    <!-- <div class="flex-1 flex items-center gap-2"> -->
  <!-- Option Icon -->
  <!-- @if (option.icon) {
          <app-icon-v2 [name]="option.icon" [size]="16" class="text-gray-500 dark:text-gray-400" />
          } -->

    <!-- Option Text -->
    <!-- <div class="dropdown-option-text flex items-center justify-between w-full">
              <div class="dropdown-option-label">{{ option.label }}</div>
              @if (option.description) {
              <div class="dropdown-option-description">
                {{ option.description }}
              </div>
            } -->

    <!-- </div> -->

    <!-- Option Badge(s) -->
    <!-- <div class="flex gap-1">
              @if (option.badge) {
              <span [class]="getBadgeClasses(option.badge.variant)">
                {{ option.badge.text }}
              </span>
              }
              @for (badge of option.badges; track badge) {
              <span [class]="getBadgeClasses(badge.variant)">
                {{ badge.text }}
              </span>
              }
            </div>
          </div>
        </button>
        }
        } -->
  </div>
  }

  <!-- Error Message -->
  @if (error) {
  <div [class]="applyThemeClasses('mt-1.5 text-xs text-red-600 dark:text-red-400 flex items-center gap-1')" role="alert">
    <app-icon-v2 name="circle-alert" size="14" />
    <span>{{ error }}</span>
  </div>
  }

  <!-- Hint Message -->
  @if (hint && !error) {
  <div class="dropdown-hint">
    {{ hint }}
  </div>
  }
</div>